import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CreateInviteDto,
  DashboardIndicators,
  PapelOption,
  UpdateUserDto,
  hideGlobalDialogErrorContext,
} from '@aionzdev/modules';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Usuario, UsuarioEmpresa } from '../../models/usuario';
import { PAPEL_EMPRESA_LABEL, PapelEmpresa } from '../../models/papel-empresa.enum';
import { EmpresaComContexto } from '../../models/empresa-com-contexto';


@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly _http = inject(HttpClient);
  private readonly nodeBaseUrl = `${environment.apiUrl}/user`;
  private readonly empresaBaseUrl = `${environment.apiUrl}/empresa`;

  // GET /user/empresa/{empresaId}/usuarios
  findAllUsers(empresaId: number, params: { paginaAtual?: number; registrosPorPagina?: number; search?: string } = {}): Observable<HttpResponse<UsuarioEmpresa[]>> {
    return this._http.get<UsuarioEmpresa[]>(
      `${this.nodeBaseUrl}/empresa/${empresaId}/usuarios`,
      { params: params as Record<string, string | number>, observe: 'response' }
    );
  }

  // GET /user/empresa/{empresaId}/usuarios/indicadores
  getDashboardIndicators(empresaId: number): Observable<DashboardIndicators> {
    return this._http.get<DashboardIndicators>(
      `${this.nodeBaseUrl}/empresa/${empresaId}/usuarios/indicadores`
    );
  }

  // POST /user/update
  update(data: UpdateUserDto): Observable<Usuario> {
    return this._http.post<Usuario>(`${this.nodeBaseUrl}/update`, {
      id: Number(data.id),
      nome: data.name,
      email: data.email,
      telefone1: data.phone,
      cpf: data.cpf,
      bio: data.userAttributes?.find(attribute => attribute.key === 'bio')?.value,
      avatar: data.avatar?.startsWith('data:') ? undefined : data.avatar,
    });
  }

  // POST /user/edit-avatar
  uploadAvatar(file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('avatar', file, file.name);
    return this._http.post<{ url: string }>(`${this.nodeBaseUrl}/edit-avatar`, formData);
  }

  // MOCK — endpoint não disponível na API
  deactivateAccountAdmin(_id: string): Observable<boolean> {
    return of(false);
  }

  // GET /user/me/empresas
  // Rota "gorda": todas as empresas do usuário com o contexto derivado
  // (papel, integração ERP, agentes exclusivos). Chamada uma única vez no
  // login e cacheada pelo TenantContextService.
  getMyCompanies(): Observable<EmpresaComContexto[]> {
    return this._http
      .get<{ empresas: EmpresaComContexto[] }>(`${this.nodeBaseUrl}/me/empresas`)
      .pipe(map(r => r.empresas));
  }

  // GET /user/usuario/{id}
  findById(id: number): Observable<Usuario> {
    return this._http.get<Usuario>(`${this.nodeBaseUrl}/usuario/${id}`);
  }

  // GET /user/find-by-cpf/{cpf}
  findByCpf(cpf: string): Observable<Usuario> {
    return this._http.get<Usuario>(`${this.nodeBaseUrl}/findByCpf/${cpf}`);
  }

  // GET /usuario/findByCpf/{cnpj}
  // A busca automática por CNPJ trata "não encontrado" (404) como fluxo normal:
  // o usuário apenas preenche os campos manualmente. Por isso desativamos o
  // diálogo global de erro do @aionzdev/modules para esta requisição.
  findByCNPJ(cnpj: string): Observable<Usuario | null> {
    return this._http
      .get<Usuario>(`${this.nodeBaseUrl}/findByCpf/${cnpj}`, {
        context: hideGlobalDialogErrorContext,
      })
      .pipe(
        take(1),
        catchError(() => of(null))
      );
  }

  // MOCK — endpoint não disponível na API
  export(): Observable<{ message: string }> {
    return of({ message: '' });
  }

  // POST /empresa/{empresaId}/convites
  createInvite(empresaId: number, data: CreateInviteDto): Observable<unknown> {
    return this._http.post(`${this.empresaBaseUrl}/${empresaId}/convites`, {
      email: data.username,
      papel: data.papel
    });
  }

  // GET /empresa/{empresaId}/convites/papeis-disponiveis
  getAvailablePapeis(empresaId: number): Observable<PapelOption[]> {
    return this._http
      .get<PapelEmpresa[]>(`${this.empresaBaseUrl}/${empresaId}/convites/papeis-disponiveis`)
      .pipe(
        map(papeis =>
          papeis.map(papel => ({ label: PAPEL_EMPRESA_LABEL[papel], value: papel }))
        )
      );
  }
}
